import type { McpConnectorWire } from '../types';

export interface McpConnectorCardProps {
  connector: McpConnectorWire;
  mode: 'manage' | 'read-only';
  onRefresh?: () => void;
  onEditCredential?: () => void;
  onDelete?: () => void;
  onSeeErrorDetails?: () => void;
}

/**
 * Sprint A4 S1 PR 3 — Task 5.
 *
 * McpConnectorCard renderiza 1 conector em 1 dos 5 estados canonical
 * (pending-handshake/active/failed/paused/disabled). `disabled` retorna
 * null — o filter já acontece no service backend (`status: { not: 'disabled' }`),
 * mas a guarda dupla cobre race condition.
 *
 * Modo `read-only` (DPO Regulated com `integrations-read`) esconde botões de
 * ação. Defense-in-depth: backend rejeita POST/PATCH/DELETE via capability
 * guard mesmo se o DOM for manipulado (frontend é só UX, não autoridade).
 */

const STATUS_VISUAL: Record<
  McpConnectorWire['status'],
  { icon: string; color: string; label: string }
> = {
  'pending-handshake': { icon: '⏳', color: 'text-blue-600', label: 'Testando conexão...' },
  active: { icon: '✓', color: 'text-green-600', label: 'Conectado' },
  failed: { icon: '⚠', color: 'text-orange-600', label: 'Falha ao conectar' },
  paused: { icon: '⏸', color: 'text-gray-500', label: 'Pausada pela feature global' },
  disabled: { icon: '🚫', color: 'text-gray-400', label: 'Removida' },
};

export function McpConnectorCard({
  connector,
  mode,
  onRefresh,
  onEditCredential,
  onDelete,
  onSeeErrorDetails,
}: McpConnectorCardProps): JSX.Element | null {
  if (connector.status === 'disabled') return null;

  const visual = STATUS_VISUAL[connector.status];
  const toolsCount = connector.discoveredTools?.length ?? 0;

  return (
    <div className="rounded-lg border border-border-subtle bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-text-primary">{connector.displayName}</h3>
          <p className="mt-1 truncate text-xs text-text-secondary">{connector.serverUrl}</p>
          <p className="mt-0.5 text-xs text-text-secondary">
            <code className="font-mono">{connector.slug}</code> ·{' '}
            <code className="font-mono">{connector.namespace}</code>
          </p>
        </div>
        <span className={`${visual.color} shrink-0 text-sm font-medium`}>
          {visual.icon} {visual.label}
        </span>
      </div>

      {connector.status === 'active' && toolsCount > 0 && (
        <p className="mt-2 text-xs text-text-secondary">
          {toolsCount} tools detectadas: {connector.discoveredTools?.join(', ')}
        </p>
      )}

      {connector.status === 'failed' && connector.lastHandshakeError !== null && (
        <p className="mt-2 text-xs text-orange-700">
          &ldquo;{connector.lastHandshakeError.message}&rdquo; ·{' '}
          <button type="button" onClick={onSeeErrorDetails} className="underline">
            Ver detalhes
          </button>
        </p>
      )}

      {connector.lastHandshakeAt !== null && (
        <p className="mt-2 text-xs text-text-secondary">
          Última conexão: {new Date(connector.lastHandshakeAt).toLocaleString('pt-BR')}
        </p>
      )}

      {mode === 'manage' &&
        connector.status !== 'paused' &&
        connector.status !== 'pending-handshake' && (
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={onRefresh}
              className="rounded border border-border-subtle px-3 py-1 text-xs hover:bg-bg-muted"
            >
              {connector.status === 'failed' ? 'Testar de novo' : 'Testar conexão'}
            </button>
            <button
              type="button"
              onClick={onEditCredential}
              className="rounded border border-border-subtle px-3 py-1 text-xs hover:bg-bg-muted"
            >
              Trocar credencial
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="rounded border border-border-subtle px-3 py-1 text-xs text-red-600 hover:bg-red-50"
            >
              Remover
            </button>
          </div>
        )}
    </div>
  );
}
